import React, { useEffect } from 'react';
import { Select } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  clearSelectedCompany,
  setAssignedCompanies,
  setSelectedCompany,
} from '../../store/slices/adminContextSlice';
import api from '../../config/api';

interface Props {
  /** Compact mode for mobile drawer */
  compact?: boolean;
  /** Override width (default 280px on desktop) */
  width?: number | string;
}

const AdminCompanySelector: React.FC<Props> = ({ compact = false, width }) => {
  const dispatch = useAppDispatch();
  const { assignedCompanies, selectedCompanyId, loaded } =
    useAppSelector((s) => s.adminContext);

  useEffect(() => {
    if (loaded) return;
    api
      .get('/admin-assignments/my-companies')
      .then((res) => {
        const data = res.data?.data ?? [];
        dispatch(setAssignedCompanies(data));
      })
      .catch(() => dispatch(setAssignedCompanies([])));
  }, [loaded, dispatch]);

  if (!assignedCompanies.length && loaded) return null;

  const options = assignedCompanies.map((c) => ({
    value: c.employerCompanyId,
    label: c.companyName ?? c.employerCompanyId,
  }));

  const selectWidth = width ?? (compact ? '100%' : 280);

  return (
    <Select
      allowClear
      showSearch
      filterOption={(input, option) =>
        (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
      }
      placeholder={
        <span style={{ color: '#94a3b8', fontSize: 13 }}>
          <BankOutlined style={{ marginRight: 6 }} />
          Select company…
        </span>
      }
      value={selectedCompanyId ?? undefined}
      onChange={(val, opt) => {
        if (!val) {
          dispatch(clearSelectedCompany());
        } else {
          const label = Array.isArray(opt)
            ? opt[0]?.label
            : (opt as { label: string })?.label;
          dispatch(setSelectedCompany({ id: val as string, name: label ?? null }));
        }
      }}
      options={options}
      style={{ width: selectWidth, fontSize: 13 }}
      size={compact ? 'middle' : 'small'}
      dropdownRender={(menu) => (
        <div>
          <div
            style={{
              padding: '6px 12px 4px',
              fontSize: 11,
              color: '#888',
              borderBottom: '1px solid #f0f0f0',
              marginBottom: 4,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Assigned Companies
          </div>
          {menu}
        </div>
      )}
    />
  );
};

export default AdminCompanySelector;
